import React, { useState } from 'react';
import { Card, Button, Row, Col, Typography, List, Modal, Form, Input, message, Select } from 'antd';
import { CheckOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { useLocation } from "react-router-dom";
import axios from "axios";
import './styles/SubscribePage.css';
import API_BASE_URL from "../config/api";
import countryList from "react-select-country-list";
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../hooks/useSubscription';

const { Title, Text } = Typography;
const { Option } = Select;

// Initialize Stripe with your publishable key - with safety check
const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

// Safety check for Stripe key
if (!stripePublishableKey) {
  console.error("❌ REACT_APP_STRIPE_PUBLISHABLE_KEY is not defined in environment variables");
}

const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : null;

// Country options for targeting
const allCountries = countryList().getData();
// ✅ FIX: "Global" should be FIRST OPTION + placeholder (matches MEO exactly)
const countryOptions = [
  { name: "Global", code: "" }, // ✅ First option in list (matches MEO)
  ...allCountries.map((country) => ({
    name: country.label,
    code: country.value
  }))
];

// ✅ UPDATED: Helper function to compute charge - NO FEE FOR SUBSCRIPTIONS
function computeCharge({ tier, contactTier, targetCountry }) {
  let amount =
    tier === 'starter' ? 99 :
    tier === 'growth' ? 299 :
    tier === 'scale' ? 599 :
    contactTier === '1000' ? 25 : 15;

  const hasCountryTargeting = !!targetCountry && targetCountry.trim() !== '' && targetCountry.toLowerCase() !== 'global';
  
  // ⚠️ SUBSCRIPTIONS: Country targeting is FREE (no addon)
  // ⚠️ PAYG: Country targeting adds £10 (handled in CreateCampaign.js)
  // This function is only used for subscription modal, so NO addon here

  return { amount, hasCountryTargeting };
}

const SubscribeForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: subscription, isLoading: subLoading } = useSubscription(user?.uid);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const stripe = useStripe();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [targetCountry, setTargetCountry] = useState("");
  const elements = useElements();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const selectedQueryPlan = queryParams.get("plan");

  const handleSubscribe = (plan) => {
    // For PAYG plans, go to campaigns to create
    if (plan.value.startsWith('payg')) {
      navigate('/campaigns');
    } else {
      // For subscription plans, show payment modal
      setSelectedPlan(plan);
      setIsModalVisible(true);
    }
  };

  const handleReturn = () => {
    navigate('/campaigns');
  };

  const handlePayment = async (values) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      if (!token) {
        message.error('Please log in to make a payment');
        navigate('/login');
        return;
      }

      // Compute final amount with country targeting
      const { amount: finalAmount } = computeCharge({
        tier: selectedPlan?.value,
        contactTier: selectedPlan?.value.startsWith('payg') ? selectedPlan?.value.split('_')[1] : null,
        targetCountry: targetCountry
      });

      const baseAmount = selectedPlan?.basePrice || finalAmount;

      console.log('💳 Creating subscription checkout for plan:', selectedPlan?.value);

      // Create Stripe Checkout Session for subscription
      const { data } = await axios.post(
        `${API_BASE_URL}/api/payment/create-subscription`,
        {
          subscriptionPlan: selectedPlan?.value,
          targetCountry: targetCountry || "",
          baseAmount: baseAmount,
          finalAmount: finalAmount,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const { checkoutUrl, sessionId } = data.data;
      
      if (!checkoutUrl) {
        throw new Error("Failed to create checkout session. Please try again.");
      }

      console.log('✅ Checkout session created:', sessionId);
      console.log('🔗 Redirecting to Stripe Checkout:', checkoutUrl);

      // Redirect to Stripe Checkout
      window.location.href = checkoutUrl;

    } catch (error) {
      console.error('Payment error:', error);
      message.error(error.response?.data?.error || error.message || 'Payment failed. Please try again.');
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
    setTargetCountry("");
  };

  // ✅ NEW PRICING PLANS - synchronized with CreateCampaign.js
  const plans = [
    {
      title: 'Pay-As-You-Go (500 Contacts)',
      price: '£15',
      basePrice: 15,
      period: '/campaign',
      features: [
        'AI-Written Outreach Emails',
        'Automated email sending',
        'AI Audience-Avatar Modeling',
        'AI-generated search term suggestions',
        'Instant pre-filled campaign setup',
        'Real-time campaign tracking',
        'Advanced performance analytics',
        'Unlimited Gmail accounts supported',
        'Auto-pause & auto-resume with Gmail quota',
        'Up to 500 contacts sourced and emailed',
        'Optional: Country targeting (+£5)'
      ],
      value: 'payg_500',
      buttonText: 'Get Started',
    },
    {
      title: 'Pay-As-You-Go (1,000 Contacts)',
      price: '£25',
      basePrice: 25,
      period: '/campaign',
      features: [
        'AI-Written Outreach Emails',
        'Automated email sending',
        'AI Audience-Avatar Modeling',
        'AI-generated search term suggestions',
        'Pre-filled campaign creation',
        'Real-time campaign analytics',
        'Unlimited Gmail accounts supported',
        'Auto-pause & auto-resume',
        'Priority queue processing',
        'Up to 1,000 contacts sourced and emailed',
        'Optional: Country targeting (+£5)'
      ],
      value: 'payg_1000',
      buttonText: 'Get Started',
    },
    {
      title: 'Starter Plan',
      price: '£99',
      basePrice: 99,
      period: '/month',
      features: [
        'AI-Written Outreach Emails (unlimited campaigns)',
        'AI Audience-Avatar Modeling (unlimited)',
        'AI search term suggestions (unlimited)',
        'Automated email sending',
        'Country targeting included',
        'Advanced analytics dashboard + performance graphs',
        'Unlimited Gmail accounts supported',
        'Auto-pause & auto-resume',
        'Real-time sending logs',
        'Up to 5,000 contacts/month sourced and emailed'
      ],
      value: 'starter',
      buttonText: 'Subscribe Now',
      recommended: false,
    },
    {
      title: 'Growth Plan',
      price: '£299',
      basePrice: 299,
      period: '/month',
      features: [
        'AI-Written Outreach Emails',
        'AI Audience-Avatar Modeling (unlimited)',
        'AI search term suggestions (unlimited)',
        'Automated email sending',
        'Country targeting included',
        'Up to 15,000 contacts/month sourced and emailed',
        'Unlimited Gmail accounts supported',
        'High-priority sending queue',
        'AI optimisations',
        'Advanced analytics + full performance graph',
        'Dedicated Gmail deliverability monitoring'
      ],
      value: 'growth',
      buttonText: 'Subscribe Now',
      recommended: true,
    },
    {
      title: 'Scale Plan',
      price: '£599',
      basePrice: 599,
      period: '/month',
      features: [
        'AI-Written Outreach Emails',
        'AI Audience-Avatar Modeling (unlimited)',
        'AI search term generation (unlimited)',
        'Automated email sending across all accounts',
        'Country targeting included',
        'Up to 30,000 contacts/month sourced and emailed',
        'Unlimited Gmail accounts supported',
        'Highest sending priority',
        'Dedicated success manager',
        'Exportable analytics suite',
        'Enterprise automation features',
        'White-label mode available'
      ],
      value: 'scale',
      buttonText: 'Subscribe Now',
    },
  ];

  // ✅ Filter plans based on query parameter
  const visiblePlans = selectedQueryPlan
    ? plans.filter(plan => plan.value === selectedQueryPlan)
    : plans;

  // ✅ REMOVED: finalPrice calculation (was incorrectly adding £5 for subscriptions)
  // Now using computeCharge() directly in the Price field (matches MEO)

  return (
    <div className="subscribe-container">
      <Button 
        className="return-button"
        type="primary"
        icon={<ArrowLeftOutlined />}
        onClick={handleReturn}
      >
        Back to Campaigns
      </Button>

      <div className="subscribe-header">
        <Title level={1}>Choose Your Plan</Title>
        <Text className="subtitle">
          Select the perfect plan for your email campaign needs
        </Text>
      </div>

      <Row gutter={[24, 24]} justify="center" className="pricing-row">
        {visiblePlans.map((plan) => (
          <Col xs={24} md={12} lg={6} key={plan.value}>
            <Card 
              className={`pricing-card ${plan.recommended ? 'recommended' : ''}`}
              title={
                <div className="card-header">
                  {plan.recommended && <div className="recommended-badge">RECOMMENDED</div>}
                  <Title level={2}>{plan.title}</Title>
                  <div className="price">
                    <span className="amount">{plan.price}</span>
                    <span className="period">{plan.period}</span>
                  </div>
                </div>
              }
            >
              <List
                className="features-list"
                dataSource={plan.features}
                renderItem={feature => (
                  <List.Item>
                    <CheckOutlined className="check-icon" /> {feature}
                  </List.Item>
                )}
              />
              {subscription && subscription.plan === plan.value ? (
                // User is subscribed to this plan
                <>
                  <Button
                    size="large"
                    block
                    disabled
                    style={{
                      background: '#52c41a',
                      borderColor: '#52c41a',
                      color: 'white',
                      cursor: 'not-allowed',
                      opacity: 0.9
                    }}
                  >
                    ✓ Subscribed
                  </Button>
                  {subscription.resetDateMillis && (
                    <Text style={{ display: 'block', marginTop: '8px', fontSize: '12px', color: '#666', textAlign: 'center' }}>
                      Renews: {new Date(subscription.resetDateMillis).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </Text>
                  )}
                </>
              ) : subscription && subscription.plan !== plan.value ? (
                // User is subscribed to a different plan
                <Button
                  type="default"
                  size="large"
                  block
                  disabled
                  style={{ cursor: 'not-allowed', opacity: 0.6 }}
                >
                  Change Plan (Contact Support)
                </Button>
              ) : (
                // User is not subscribed
                <Button
                  type="primary"
                  size="large"
                  block
                  className={`subscribe-button ${plan.recommended ? 'recommended-button' : ''}`}
                  onClick={() => handleSubscribe(plan)}
                  loading={loading}
                  disabled={subLoading}
                >
                  {plan.buttonText}
                </Button>
              )}
            </Card>
          </Col>
        ))}
      </Row>

      <Modal
        title="Payment Details"
        visible={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        className="payment-modal"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handlePayment}
        >
          <Form.Item
            label={<span style={{ color: '#000000' }}>Name</span>}
            name="name"
            rules={[{ required: true, message: 'Please enter your name' }]}
          >
            <Input placeholder="Enter your full name" />
          </Form.Item>

          <Form.Item 
            label={<span style={{ color: '#000000' }}>Target Country (Optional)</span>}
          >
            <Select
              value={targetCountry}
              onChange={(value) => setTargetCountry(value)}
              style={{ width: '100%' }}
              placeholder="Global (All Countries)"
              virtual={false}
              listHeight={300}
              showSearch
              allowClear
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={countryOptions.map((country) => ({
                value: country.code,
                label: country.name
              }))}
            />
          </Form.Item>

          {/* ✅ FIXED: Now uses computeCharge() directly (matches MEO) */}
          <Form.Item label={<span style={{ color: '#000000' }}>Price</span>}>
            <Input 
              value={`£${computeCharge({
                tier: selectedPlan?.value,
                contactTier: selectedPlan?.value?.startsWith('payg') ? selectedPlan?.value.split('_')[1] : null,
                targetCountry: targetCountry
              }).amount}`} 
              disabled 
              className="price-input" 
            />
          </Form.Item>

          <Form.Item label={<span style={{ color: '#000000' }}>Card Details</span>} required>
            <CardElement
              options={{
                style: {
                  base: {
                    fontSize: '16px',
                    color: '#424770',
                    '::placeholder': {
                      color: '#aab7c4',
                    },
                  },
                  invalid: {
                    color: '#9e2146',
                  },
                },
              }}
            />
          </Form.Item>

          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              block 
              size="large"
              loading={loading}
              disabled={!stripe}
            >
              Pay Now
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

// Main component with safety guard for missing Stripe key
const SubscribePage = () => {
  // Safety check: If Stripe key is missing, show error message
  if (!stripePromise) {
    return (
      <div style={{
        padding: '40px',
        textAlign: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white'
      }}>
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          padding: '40px',
          borderRadius: '12px',
          maxWidth: '500px',
          color: '#ff4d4f'
        }}>
          <h2 style={{ color: '#ff4d4f' }}>⚠️ Stripe Configuration Error</h2>
          <p style={{ color: '#333', marginTop: '20px' }}>
            The Stripe payment system is not configured correctly. 
            Please ensure REACT_APP_STRIPE_PUBLISHABLE_KEY is set in your environment variables.
          </p>
          <Button 
            type="primary" 
            onClick={() => window.location.href = '/campaigns'}
            style={{ marginTop: '20px' }}
          >
            Return to Campaigns
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <SubscribeForm />
    </Elements>
  );
};

export default SubscribePage;
